import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingHeader from '../components/MarketingHeader';

import { useLanguage } from '../contexts/LanguageContext';
import './DashboardPage.css';

function DashboardPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [showDemoModal, setShowDemoModal] = useState(false);
    const [leadData, setLeadData] = useState({ name: '', phone: '', email: '' });
    const [isSubmittingLead, setIsSubmittingLead] = useState(false);
    const [leadSuccess, setLeadSuccess] = useState(false);
    const navigate = useNavigate();
    const { t } = useLanguage();

    const handleLeadSubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingLead(true);
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const res = await fetch(`${API_URL}/api/platform/leads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(leadData)
            });
            if (res.ok) {
                setLeadSuccess(true);
                setLeadData({ name: '', phone: '', email: '' });
            } else {
                alert('Failed to submit. Please try again.');
            }
        } catch (error) {
            console.error(error);
            alert('An error occurred.');
        } finally {
            setIsSubmittingLead(false);
        }
    };

    const heroRef = useRef(null);
    const featuresRef = useRef(null);

    // Currency Rates


    // Testimonials


    // BENTO GRID FEATURES with realistic assets
    const features = [
        {
            id: 'qr',
            title: t('qrScanTitle'),
            description: t('qrScanDesc'),
            color: '#FF6B6B',
            image: '/dash-feature-qr.png',
            icon: 'fa-qrcode',
            size: 'wide',
            featureId: 'qr-ordering'
        },
        {
            id: 'kds',
            title: t('liveKDS'),
            description: t('kdsDesc'),
            color: '#FFA502',
            image: '/dash-feature-kds-new.png',
            icon: 'fa-fire',
            size: 'medium',
            featureId: 'kds'
        },
        {
            id: 'analytics',
            title: t('analyticsInsights'),
            description: t('analyticsInsightsDesc'),
            color: '#2ED573',
            image: '/dash-feature-analytics.png',
            icon: 'fa-chart-line',
            size: 'wide',
            featureId: 'analytics'
        },
        {
            id: 'tables',
            title: t('tableMgmt'),
            description: t('tableMgmtDesc'),
            color: '#A3CB38',
            image: '/dash-feature-tables.png',
            icon: 'fa-th',
            size: 'medium',
            featureId: 'table-management'
        },
        {
            id: 'menu',
            title: t('dynamicMenu'),
            description: t('dynamicMenuDesc'),
            color: '#1289A7',
            image: '/dash-feature-menu-v2.png',
            icon: 'fa-book-open',
            size: 'wide',
            featureId: 'menu-management'
        },
        {
            id: 'team',
            title: t('roleMgmt'),
            description: t('roleMgmtDesc'),
            color: '#5352ED',
            image: '/dash-feature-team.png',
            icon: 'fa-user-shield',
            size: 'medium',
            featureId: 'role-management'
        },
        {
            id: 'branches',
            title: t('branchMgmt'),
            description: t('branchMgmtDesc'),
            color: '#FF6348',
            image: '/dash-feature-branches-new.png',
            icon: 'fa-code-branch',
            size: 'wide',
            featureId: 'branch-management'
        },
        {
            id: 'inventory',
            title: t('smartInventory'),
            description: t('inventoryDesc'),
            color: '#1E90FF',
            image: '/dash-feature-inventory.png',
            icon: 'fa-cubes',
            size: 'medium',
            featureId: 'inventory'
        },
        {
            id: 'loyalty',
            title: t('loyaltyRewardsTitle'),
            description: t('loyaltyRewardsDesc'),
            color: '#14B8A6',
            image: '/rewards_system_feature.png',
            icon: 'fa-gift',
            size: 'wide',
            featureId: 'loyalty-rewards'
        },
        {
            id: 'manual-ordering',
            title: t('manualOrderingTitle'),
            description: t('manualOrderingDesc'),
            color: '#10B981',
            image: '/manual_ordering_feature.png',
            icon: 'fa-keyboard',
            size: 'medium',
            featureId: 'manual-ordering'
        }
    ];





    // Scroll & Effects

    useEffect(() => {
        const handleKeyDown = (e) => { 
            if (e.key === 'Escape') setShowDemoModal(false); 
        };
        if (showDemoModal) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [showDemoModal]);



    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add('animate-in'); });
        }, { threshold: 0.1 });
        const elements = document.querySelectorAll('.animate-on-scroll');
        elements.forEach(el => observer.observe(el));
        return () => elements.forEach(el => observer.unobserve(el));
    }, []);

    // Dropdown Outside Click


    const scrollToSection = useCallback((ref) => { ref.current?.scrollIntoView({ behavior: 'smooth' }); }, []);

    // Core Navigation Logic (Unchanged)
    const goToSignup = () => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
            navigate("/signup");
        }, 1000);
    };



    return (
        <>
        <div className="dashboard stunning-theme">
            {/* Hero Background using Inline Style to avoid Webpack errors */}
            <div className="stunning-hero-bg" style={{ backgroundImage: "url('/dash-hero-bg.png')" }}></div>

            {/* Header */}
            <MarketingHeader />

            {/* Hero Section */}
            <section ref={heroRef} id="home" className="hero">
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-text animate-on-scroll" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}>
                            <div className="hero-badge"><span>🍽️ {t('heroTagline')}</span></div>
                            <h1 className="hero-title">{t('heroTitle')}</h1>
                            <p className="hero-description">{t('heroSubtitle')}</p>
                            <div className="hero-actions">
                                <button className="btn btn-hero-primary glow-effect" onClick={goToSignup} disabled={isLoading}>
                                    {isLoading ? <><i className="fas fa-spinner fa-spin mr-2"></i> Loading...</> : t('startFreeTrial')}
                                </button>
                                <button className="btn btn-hero-secondary" onClick={() => setShowDemoModal(true)}><i className="fas fa-play"></i> {t('watchDemo')}</button>
                            </div>
                            <div className="hero-stats">
                                <div className="stat"><div className="stat-number">40%</div><div className="stat-label">{t('fasterService')}</div></div>
                                <div className="stat"><div className="stat-number">25%</div><div className="stat-label">{t('higherRevenue')}</div></div>
                            </div>
                        </div>
                        <div className="hero-visual animate-on-scroll">
                            <div className="realistic-mockup-container">
                                <img src="/dash-hero-visual.png" alt="App on Phone" className="hero-realistic-image" />
                                <div className="floating-card card-1 glass-card"><i className="fas fa-check-circle"></i><span>Order Scanned</span></div>
                                <div className="floating-card card-2 glass-card"><i className="fas fa-bell"></i><span>Chef Notified</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bento Grid Features Section */}
            <section ref={featuresRef} id="features" className="features">
                <div className="container">
                    <div className="section-header animate-on-scroll">
                        <h2 className="section-title">{t('completeEcosystem')}</h2>
                        <p className="section-subtitle">{t('stunningSuite')}</p>
                    </div>

                    <div className="bento-grid">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className={`feature-card glass-card bento-${feature.size} animate-on-scroll`}
                                onClick={() => {
                                    // Map dashboard feature IDs to features page IDs
                                    const featureMapping = {
                                        'qr': 'qr-ordering',
                                        'kds': 'kds',
                                        'analytics': 'analytics',
                                        'tables': 'table-management',
                                        'menu': 'menu-management',
                                        'inventory': 'inventory',
                                        'team': 'role-management',
                                        'branches': 'branch-management'
                                    };
                                    const targetFeature = featureMapping[feature.id] || 'qr-ordering';
                                    navigate(`/features?feature=${targetFeature}`);
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="feature-image-wrapper">
                                    <img src={feature.image} alt={feature.title} className="feature-realistic-img" />
                                    <div className="feature-overlay"></div>
                                </div>
                                <div className="feature-details">
                                    <div className="feature-icon" style={{ backgroundColor: feature.color }}>
                                        <i className={`fas ${feature.icon}`}></i>
                                    </div>
                                    <h3>{feature.title}</h3>
                                    <p>{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Interest / Lead Form Section */}
            <section className="py-20 relative bg-[#0B1120] border-t border-white/5 overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-[500px] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none opacity-50"></div>
                <div className="marketing-container relative z-10 max-w-4xl">
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-10 md:p-14 shadow-2xl">
                        {!leadSuccess && (
                            <div className="text-center mb-10">
                                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                                    Interested in RedSorm?
                                </h2>
                                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                                    Leave your details below and our team will get in touch with you shortly to set up a personalized demo.
                                </p>
                            </div>
                        )}

                        {leadSuccess ? (
                            <div className="flex flex-col items-center justify-center p-8 text-center animate-in zoom-in duration-300">
                                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                                    <i className="fas fa-check text-4xl text-green-400"></i>
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-2">Thanks for your interest!</h3>
                                <p className="text-gray-400 text-lg">We will contact you soon.</p>
                                <button onClick={() => setLeadSuccess(false)} className="mt-8 px-6 py-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors">
                                    Submit Another Request
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleLeadSubmit} className="max-w-2xl mx-auto space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-gray-300 text-sm font-semibold mb-2 ml-1">Full Name *</label>
                                        <input 
                                            type="text" 
                                            required
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                                            placeholder="John Doe"
                                            value={leadData.name}
                                            onChange={(e) => setLeadData({...leadData, name: e.target.value})}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-300 text-sm font-semibold mb-2 ml-1">Phone Number *</label>
                                        <input 
                                            type="tel" 
                                            required
                                            pattern="^[\+]?[0-9\s\-\(\)]{10,15}$"
                                            title="Please enter a valid phone number (e.g. +91 98765 43210)"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                                            placeholder="+91 98765 43210"
                                            value={leadData.phone}
                                            onChange={(e) => setLeadData({...leadData, phone: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-gray-300 text-sm font-semibold mb-2 ml-1">Email Address (Optional)</label>
                                    <input 
                                        type="email" 
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                                        placeholder="john@restaurant.com"
                                        value={leadData.email}
                                        onChange={(e) => setLeadData({...leadData, email: e.target.value})}
                                    />
                                </div>
                                
                                <button 
                                    type="submit" 
                                    disabled={isSubmittingLead}
                                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-bold py-4 rounded-xl transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] disabled:opacity-50 text-lg mt-4"
                                >
                                    {isSubmittingLead ? <><i className="fas fa-spinner fa-spin mr-2"></i> Submitting...</> : 'Request a Callback'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>



            {/* Advanced Footer */}
            <footer className="footer-advanced">
                <div className="container">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-16 mb-16">
                        <div className="footer-brand col-span-2 lg:col-span-1 text-center lg:text-left flex flex-col items-center lg:items-start">
                            <div className="logo footer-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    background: '#fff',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    padding: '4px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                }}>
                                    <img src="/app-logo.png" alt="RedSorm" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </div>
                                <span className="logo-text">RedSorm</span>
                            </div>
                            <p>{t('joinRevolution')}</p>
                             <div className="social-icons">
                                 <a href="https://x.com/Redsorm_S" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="X (Twitter)"><i className="fab fa-twitter"></i></a>
                                 <a href="https://www.facebook.com/profile.php?id=61590644184687" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook"><i className="fab fa-facebook"></i></a>
                                 <a href="https://www.instagram.com/redsorm.in/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
                                 <a href="https://www.youtube.com/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="YouTube"><i className="fab fa-youtube"></i></a>
                                 <a href="https://www.linkedin.com/company/117164495/admin/dashboard/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn"><i className="fab fa-linkedin"></i></a>
                             </div>
                        </div>

                        <div className="footer-links-col col-span-1">
                            <h4>{t('product')}</h4>
                            <ul>
                                <li><a href="/features" onClick={(e) => { e.preventDefault(); navigate('/features'); }}>{t('features')}</a></li>
                                <li><a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>{t('pricing')}</a></li>
                                <li><a href="/faqs" onClick={(e) => { e.preventDefault(); navigate('/faqs'); }}>{t('faqs')}</a></li>
                                <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection(featuresRef); }}>Overview</a></li>
                            </ul>
                        </div>

                        <div className="footer-links-col col-span-1">
                            <h4>{t('company')}</h4>
                            <ul>
                                <li><a href="/about" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>About Us</a></li>
                                <li><button className="text-gray-400 hover:text-white transition-colors" onClick={(e) => e.preventDefault()}>Careers</button></li>
                                <li><button className="text-gray-400 hover:text-white transition-colors" onClick={(e) => e.preventDefault()}>Blog</button></li>
                            </ul>
                        </div>

                        <div className="footer-newsletter col-span-2 lg:col-span-1 text-center lg:text-left">
                            <h4>{t('stayAhead')}</h4>
                            <p>{t('getLatestFeatures')}</p>
                            <form className="newsletter-form" onSubmit={(e) => { e.preventDefault(); alert("Thanks for subscribing!"); }}>
                                <input type="email" placeholder="Enter your email" required />
                                <button type="submit" className="btn-icon"><i className="fas fa-arrow-right"></i></button>
                            </form>
                        </div>
                    </div>

                    <div className="footer-bottom">
                        <p>&copy; {new Date().getFullYear()} RedSorm Inc. All rights reserved.</p>
                        <div className="footer-legal">
                            <a href="/privacy-policy" onClick={(e) => { e.preventDefault(); navigate('/privacy-policy'); }}>{t('privacyPolicy')}</a>
                            <a href="/terms-of-service" onClick={(e) => { e.preventDefault(); navigate('/terms-of-service'); }}>{t('termsOfService')}</a>
                            <a href="/cookie-policy" onClick={(e) => { e.preventDefault(); navigate('/cookie-policy'); }}>{t('cookiePolicy')}</a>
                        </div>
                        <div className="footer-spacer"></div>
                    </div>
                </div>
            </footer>
        </div>


        {/* ─── DEMO VIDEO MODAL ─── */}
        {showDemoModal && (
            <div
                style={{
                    position: 'fixed', inset: 0, zIndex: 999999,
                    background: 'rgba(0,0,0,0.92)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                    animation: 'fadeInModal 0.3s ease',
                }}
                onClick={() => setShowDemoModal(false)}
            >
                <style>{`
                    @keyframes fadeInModal { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
                    @keyframes slideUpModal { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
                    .demo-modal-inner { animation: slideUpModal 0.35s cubic-bezier(0.34,1.56,0.64,1); }
                    .demo-close-btn:hover { background: rgba(255,255,255,0.2) !important; transform: scale(1.1); }
                    .demo-modal-img { border-radius: 12px; display: block; width: 100%; height: auto; max-height: 78vh; object-fit: contain; }
                    .demo-modal-label { display: flex; align-items: center; gap: 8px; color: rgba(255,255,255,0.5); font-size: 0.75rem; margin-top: 12px; letter-spacing: 0.08em; text-transform: uppercase; }
                    .demo-modal-label span { width: 8px; height: 8px; background: #22c55e; border-radius: 50%; display: inline-block; box-shadow: 0 0 8px #22c55e; animation: pulse 1.5s infinite; }
                    @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
                `}</style>

                <div
                    className="demo-modal-inner"
                    style={{ position: 'relative', width: '90vw', maxWidth: '1100px', padding: '0' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header bar */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#fff', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3px', flexShrink: 0 }}>
                                <img src="/app-logo.png" alt="RedSorm" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                            <div>
                                <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>RedSorm — Full Product Demo</div>
                                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', marginTop: '2px' }}>Admin Dashboard · Kitchen Display · QR Ordering · Analytics</div>
                            </div>
                        </div>
                        <button
                            className="demo-close-btn"
                            onClick={() => setShowDemoModal(false)}
                            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: '1rem', transition: 'all 0.2s' }}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {/* HTML5 Video Player */}
                    <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 40px 100px rgba(0,0,0,0.6)', background: '#000', aspectRatio: '16/9' }}>
                        <video
                            controls
                            preload="auto"
                            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                        >
                            <source src="/redsorm_platform_demo.mp4?v=3" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                        {/* Fallback if video not yet generated */}
                        <div style={{ display:'none', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'20px', padding:'60px', background:'#0a0f1e', height:'100%', position:'absolute', inset:0 }}>
                            <div style={{ width:'80px', height:'80px', borderRadius:'50%', background:'rgba(34,197,94,0.1)', border:'2px solid rgba(34,197,94,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <i className="fas fa-video" style={{ fontSize:'2rem', color:'#22c55e' }}></i>
                            </div>
                            <div style={{ textAlign:'center' }}>
                                <p style={{ color:'#fff', fontWeight:700, fontSize:'1.1rem', margin:'0 0 8px' }}>Demo video generating...</p>
                                <p style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.85rem', margin:0 }}>Run <code style={{background:'rgba(255,255,255,0.1)',padding:'2px 8px',borderRadius:'4px',fontFamily:'monospace'}}>node make_demo_video.js</code> from dineflowreact/</p>
                            </div>
                        </div>
                    </div>

                    {/* Footer CTA row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="demo-modal-label" style={{ margin: 0 }}>
                            <span></span> Product Walkthrough Showcase · Press Esc to close
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

export default DashboardPage;